<template>
  <div class="home-enhanced">
    <!-- Hero — door-aware (design doc §8: the architecture hero is retired).
         A chosen door shows that door's promise and CTA; the explicit skip
         shows a neutral hero whose body is the five doors as compact cards,
         so the choice is always one click away without the overlay. -->
    <section class="hero-enhanced" :class="{ 'hero-enhanced--door': heroDoor }">
      <div class="hero-video-background">
        <video
          autoplay
          muted
          loop
          playsinline
          class="hero-bg-video"
        >
          <source :src="heroVideo" type="video/mp4">
        </video>
        <div class="hero-overlay"/>
      </div>

      <div class="container">
        <!-- Door hero -->
        <div v-if="heroDoor" class="hero-door">
          <p class="hero-door-you">
            <span class="hero-door-you-label">{{ $t('doors.common.youSaid') }}</span>
            <span class="hero-door-you-value">“{{ $t(heroDoor.labelKey) }}”</span>
            <span class="hero-door-you-dot" aria-hidden="true">·</span>
            <button type="button" class="hero-door-change" @click="openSelector">
              {{ $t('persona.switcher.change') }}
            </button>
          </p>
          <h1 class="hero-door-title">{{ $t(`doors.${heroDoor.id}.hero.title`) }}</h1>
          <p class="hero-door-lead">{{ $t(`doors.${heroDoor.id}.hero.lead`) }}</p>
          <div class="hero-door-ctas">
            <a v-if="heroDoor.cta.external" :href="heroDoor.cta.to" class="btn btn-primary btn-xl">
              <i :class="heroDoor.cta.icon"/>
              {{ $t(heroDoor.cta.labelKey) }}
            </a>
            <router-link v-else :to="heroDoor.cta.to" class="btn btn-primary btn-xl">
              <i :class="heroDoor.cta.icon"/>
              {{ $t(heroDoor.cta.labelKey) }}
            </router-link>
            <router-link :to="heroDoor.slug" class="btn btn-outline-light btn-xl">
              <i class="fas fa-door-open"/>
              {{ $t('doors.common.learnMore') }}
            </router-link>
          </div>
        </div>

        <!-- Neutral hero: the five doors inline -->
        <div v-else class="hero-neutral">
          <h1 class="hero-neutral-title">
            <span class="hero-neutral-brand">DenvStack</span>
            {{ $t('home.neutral.title') }}
          </h1>
          <p class="hero-neutral-lead">{{ $t('home.neutral.lead') }}</p>
          <p class="hero-neutral-pick">{{ $t('home.neutral.pick') }}</p>
          <div class="hero-doors">
            <router-link
              v-for="door in doors"
              :key="door.id"
              :to="door.slug"
              class="hero-door-card"
              @click="setPersona(door.id)"
            >
              <span class="hero-door-card-jamb" aria-hidden="true"/>
              <span class="hero-door-card-text">
                <span class="hero-door-card-label">{{ $t(door.labelKey) }}</span>
                <span class="hero-door-card-next">{{ $t(door.nextKey) }}</span>
              </span>
            </router-link>
          </div>
          <div class="hero-neutral-ctas">
            <router-link to="/marketplace" class="btn btn-outline-light">
              <i class="fas fa-store"/>
              {{ $t('home.neutral.browse') }}
            </router-link>
            <button class="btn btn-outline-light" @click="scrollToDemo">
              <i class="fas fa-play-circle"/>
              {{ $t('home.neutral.demo') }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- "In action" — door-driven (src/content/doors.js renderHome): which
         recording leads, its caption and its bullets follow the door; doors A
         and B and the neutral homepage get the plain captions. -->
    <section id="demo" class="video-demo-section">
      <div class="container">
        <div class="section-header">
          <h2>{{ home.demo.title }}</h2>
          <p>{{ home.demo.lead }}</p>
        </div>

        <div class="video-grid">
          <article
            v-for="(video, i) in home.demo.videos"
            :key="video.key"
            class="video-card"
            :class="{ 'video-card--lead': i === 0 }"
            :data-video="video.key"
          >
            <div class="video-wrapper">
              <video
                controls
                preload="metadata"
                :poster="videoPoster(video.key)"
                class="video-player"
                :aria-label="video.title"
              >
                <source :src="videoSource(video.key)" type="video/mp4">
              </video>
            </div>
            <div class="video-info">
              <h3>{{ video.title }}</h3>
              <p>{{ video.body }}</p>
              <ul class="video-highlights">
                <li v-for="point in video.points" :key="point">
                  <i class="fas fa-check" aria-hidden="true"/> {{ point }}
                </li>
              </ul>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- Workflow — a real sequence: one row of four with connectors between
         the step markers on desktop, a vertical list with a connector line on
         ≤768px. The last step has no outgoing connector, so nothing points at
         empty space. -->
    <section class="integration-flow" :data-flow="home.id">
      <div class="container">
        <div class="section-header">
          <h2>{{ home.flow.title }}</h2>
          <p>{{ home.flow.lead }}</p>
        </div>

        <ol class="flow-track">
          <li v-for="step in home.flow.steps" :key="step.n" class="flow-step">
            <span class="flow-node" aria-hidden="true">{{ step.n }}</span>
            <div class="flow-text">
              <h3>{{ step.title }}</h3>
              <p>{{ step.body }}</p>
            </div>
          </li>
        </ol>
      </div>
    </section>

    <!-- Pricing — live (GET /api/cluster/plans via plans-source.js), rendered
         by the same DoorPlans component as the door pages. A door shows its
         emphasised plans; browsing shows the first three of the catalogue.
         One plan renders beside a compact "See all plans" card. -->
    <section
      v-if="plansLoading || plans.length > 0 || homeLicence"
      class="pricing-tiers"
    >
      <div class="container">
        <div class="section-header">
          <h2>Pricing for every team size</h2>
          <p v-if="heroDoor && heroDoor.plans.length">{{ $t(`pricing.doorNote.${heroDoor.id}`) }}</p>
          <p v-else-if="homeLicence">{{ homeLicence.body }}</p>
          <p v-else>Transparent pricing with no hidden fees. All plans include core services.</p>
        </div>

        <div class="home-plans">
          <DoorPlans
            :plans="sortedPlans"
            :door-plans="homeDoorPlans"
            :loading="plansLoading"
            :currency="currency"
            :licence="homeLicence"
            align="center"
            @select="selectPlan"
          />
        </div>

        <div v-if="!plansLoading && !homeLicence" class="pricing-custom-cta">
          <p>Need a custom plan, dedicated infrastructure, or special compliance?</p>
          <router-link to="/contact" class="btn btn-outline">
            <i class="fas fa-headset"/>
            Contact sales
          </router-link>
        </div>
      </div>
    </section>

    <!-- Call to Action -->
    <section class="final-cta">
      <div class="container">
        <div v-if="heroDoor" class="cta-box animate__animated animate__fadeInUp">
          <p class="cta-lead">{{ $t('home.finalCta.doorLead', { door: $t(heroDoor.labelKey) }) }}</p>
          <h2>{{ $t(`doors.${heroDoor.id}.footer.title`) }}</h2>
          <p>{{ $t(`doors.${heroDoor.id}.footer.body`) }}</p>
          <div class="cta-buttons">
            <a v-if="heroDoor.cta.external" :href="heroDoor.cta.to" class="btn btn-primary btn-large">
              <i :class="heroDoor.cta.icon"/>
              {{ $t(heroDoor.cta.labelKey) }}
            </a>
            <router-link v-else :to="heroDoor.cta.to" class="btn btn-primary btn-large">
              <i :class="heroDoor.cta.icon"/>
              {{ $t(heroDoor.cta.labelKey) }}
            </router-link>
            <router-link :to="heroDoor.slug" class="btn btn-outline-light btn-large">
              <i class="fas fa-door-open"/>
              {{ $t('doors.common.learnMore') }}
            </router-link>
          </div>
        </div>
        <div v-else class="cta-box animate__animated animate__fadeInUp">
          <h2>{{ $t('home.finalCta.neutralTitle') }}</h2>
          <p>{{ $t('home.finalCta.neutralBody') }}</p>
          <div class="cta-buttons">
            <button type="button" class="btn btn-primary btn-large" @click="openSelector">
              <i class="fas fa-door-open"/>
              {{ $t('home.neutral.pick') }}
            </button>
            <router-link to="/pricing" class="btn btn-outline-light btn-large">
              <i class="fas fa-tags"/>
              {{ $t('doors.common.seeAllPlans') }}
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- First-visit selector: "/" only, only while nothing is stored in
         cs.persona. Esc / close / "Just browsing" store the skip. -->
    <PersonaSelector v-if="showSelector" @done="onSelectorDone"/>
  </div>
</template>

<script>
import heroVideo from '@/assets/media/hero-background.mp4'
import demoVideo1 from '@/assets/media/demo-video-1.mp4'
import demoVideo2 from '@/assets/media/demo-video-2.mp4'
import { clusterApi } from '@/services/cluster-api'
import PersonaSelector from '@/components/persona/PersonaSelector.vue'
import DoorPlans from '@/components/persona/DoorPlans.vue'
import { renderHome } from '@/content/doors'
import { DOORS, usePersona } from '@/utils/persona'
import {
  loadPlansCached, sortPlans, tierKeyOf,
  CURRENCY_STORAGE_KEY, SUPPORTED_CURRENCIES
} from '@/utils/plans-source'

export default {
  name: 'HomeEnhanced',
  components: { PersonaSelector, DoorPlans },
  setup() {
    // Persona doors — shared reactive state (src/utils/persona.js).
    const { persona, door, asked, setPersona } = usePersona()
    return { persona, personaDoor: door, personaAsked: asked, setPersona }
  },
  data() {
    return {
      // Live plan catalog — the same GET /api/cluster/plans Pricing.vue uses,
      // through plans-source.js so the surfaces can't drift.
      plans: [],
      plansLoading: true,
      currency: 'EUR',
      doors: DOORS,
      // The selector is shown once per browser; `selectorDismissed` covers
      // the current page load when storage is blocked.
      selectorDismissed: false,
      selectorForced: false,

      // Videos (keep from assets)
      heroVideo,
      demoVideo1,
      demoVideo2,

      // Video Thumbnails (poster images)
      videoThumbnail1: '/media/recraft/video-thumbnails/deployment-demo-thumbnail.png',
      videoThumbnail2: '/media/recraft/video-thumbnails/ai-remediation-thumbnail.png',

      // New Recraft images (from public folder)
      heroBackground: '/media/recraft/hero/hero-tech-network.png',
      heroBackgroundSvg: '/media/recraft/hero/hero-abstract-flow.svg',

      // Architecture diagram and the service suite moved to Technical.vue
      // (design doc §8).

      // Logo
      logo: '/media/recraft/logo/denvstack-icon.svg',
      logoDark: '/media/recraft/logo/denvstack-icon-dark.svg',

      // Textures
      textureGrid: '/media/recraft/textures/texture-grid.svg',
      textureParticles: '/media/recraft/textures/texture-particles.png'
    }
  },
  computed: {
    /** The chosen door (null for "Just browsing" or never asked). */
    heroDoor() {
      return this.personaDoor
    },
    /** Door-driven copy for the "in action" and workflow sections. */
    home() {
      return renderHome(this.heroDoor ? this.heroDoor.id : null, (key, params) => this.$t(key, params))
    },
    /** Selector: "/" only, first visit only (or when the hero asks to change). */
    showSelector() {
      if (this.selectorForced) return true
      if (this.selectorDismissed) return false
      return this.$route.path === '/' && !this.personaAsked
    },
    sortedPlans() {
      return sortPlans(this.plans, this.currency)
    },
    /**
     * The tier_keys DoorPlans emphasises on Home: the door's own list, or —
     * browsing — the first three of the sorted catalogue (memberships first,
     * then workspaces by price), the rest behind "Need more?".
     */
    homeDoorPlans() {
      const door = this.heroDoor
      if (door) return door.plans
      return this.sortedPlans.slice(0, 3).map(tierKeyOf)
    },
    /** Door E: the licence block instead of plan cards. */
    homeLicence() {
      const door = this.heroDoor
      if (!door || door.plans.length) return null
      return {
        title: this.$t(`doors.${door.id}.licenceBlock.title`),
        body: this.$t(`doors.${door.id}.licenceBlock.body`),
        cta: this.$t(door.cta.labelKey),
        to: door.cta.to
      }
    }
  },
  mounted() {
    // Initialize animations
    this.$store.dispatch('ui/handleResize')

    // Pick up the user's currency choice from Pricing.vue if they made one.
    try {
      const saved = localStorage.getItem(CURRENCY_STORAGE_KEY)
      if (saved && SUPPORTED_CURRENCIES.includes(saved)) {
        this.currency = saved
      }
    } catch (_) {
      // localStorage may be unavailable — default to EUR.
    }
    this.loadPlans()
  },
  methods: {
    scrollToDemo() {
      const element = document.getElementById('demo')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    },
    openSelector() {
      this.selectorForced = true
    },
    onSelectorDone() {
      this.selectorForced = false
      this.selectorDismissed = true
    },
    async loadPlans() {
      this.plansLoading = true
      try {
        const data = await loadPlansCached(() => clusterApi.getPlans())
        this.plans = Array.isArray(data) ? data : []
      } catch (error) {
        // On marketing surface, fail silently — the section hides itself
        // (v-if on the wrapper), no scary banner.
        console.error('Home: failed to load plans:', error)
        this.plans = []
      } finally {
        this.plansLoading = false
      }
    },
    videoSource(key) {
      return key === 'remediation' ? this.demoVideo2 : this.demoVideo1
    },
    videoPoster(key) {
      return key === 'remediation' ? this.videoThumbnail2 : this.videoThumbnail1
    },
    /** Same hand-off as the door pages: Starter → sign-up, a workspace → /buy. */
    selectPlan(plan) {
      if (plan.tier_type === 'membership') {
        this.$store.commit('setAuthModalType', 'register')
        this.$store.commit('setShowAuthModal', true)
        return
      }
      this.$router.push({ path: '/buy', query: { plan_id: plan.id, currency: this.currency } })
    }
  }
}
</script>

<style scoped>
/* ==================== Global Styles ==================== */
.home-enhanced {
  min-height: 100vh;
  overflow-x: hidden;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

/* ==================== Hero Section Enhanced ==================== */
.hero-enhanced {
  position: relative;
  min-height: min(100vh, 860px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding-top: 80px;
}

.hero-video-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
}

.hero-bg-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.4) blur(2px);
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(10, 37, 64, 0.85);
  z-index: 2;
}

.hero-enhanced .container {
  position: relative;
  z-index: 3;
  width: 100%;
  text-align: center;
}

.hero-enhanced-content {
  color: white;
  max-width: 900px;
  margin: 0 auto;
}

.btn-xl {
  padding: 1rem 2.5rem !important;
  font-size: 1.1rem !important;
  gap: 0.75rem !important;
}

/* ==================== Hero: door-aware ==================== */
.hero-enhanced .container { text-align: left; }

.hero-door { max-width: 880px; color: var(--ds-white); }

.hero-door-you {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.6rem;
  margin: 0 0 1.25rem;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.75);
}
.hero-door-you-value { color: var(--ds-white); font-weight: 600; }
.hero-door-you-dot { color: rgba(255, 255, 255, 0.4); }
.hero-door-change {
  appearance: none;
  background: none;
  border: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.5);
  color: rgba(255, 255, 255, 0.85);
  font: inherit;
  font-size: 0.85rem;
  padding: 0;
  cursor: pointer;
}
.hero-door-change:hover { color: var(--ds-white); border-color: var(--ds-white); }
.hero-door-change:focus-visible { outline: 2px solid var(--ds-accent); outline-offset: 3px; }

.hero-door-title {
  font-family: var(--ds-font-display);
  font-weight: 700;
  font-size: clamp(1.9rem, 1.2rem + 2.8vw, 3.4rem);
  line-height: 1.1;
  letter-spacing: -0.02em;
  margin: 0 0 1.25rem;
  max-width: 24ch;
  color: var(--ds-white);
}
.hero-door-lead {
  font-size: clamp(1.02rem, 0.95rem + 0.45vw, 1.3rem);
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);
  max-width: 58ch;
  margin: 0 0 2rem;
}
.hero-door-ctas { display: flex; flex-wrap: wrap; gap: 1rem; }

/* Neutral hero: the doors inline */
.hero-neutral { max-width: 1040px; color: var(--ds-white); }
.hero-neutral-title {
  font-family: var(--ds-font-display);
  font-weight: 700;
  font-size: clamp(1.7rem, 1.1rem + 2.4vw, 3rem);
  line-height: 1.12;
  letter-spacing: -0.02em;
  margin: 0 0 1rem;
  max-width: 26ch;
  color: var(--ds-white);
}
.hero-neutral-brand {
  display: block;
  font-size: 0.5em;
  font-weight: 600;
  letter-spacing: 0;
  color: var(--ds-primary-400);
  margin-bottom: 0.4rem;
}
.hero-neutral-lead {
  font-size: clamp(1rem, 0.95rem + 0.35vw, 1.15rem);
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.82);
  max-width: 62ch;
  margin: 0 0 1.75rem;
}
.hero-neutral-pick {
  margin: 0 0 0.75rem;
  font-weight: 600;
  color: var(--ds-white);
}

.hero-doors {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin-bottom: 1.75rem;
}
@media (min-width: 541px) { .hero-doors { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (min-width: 1025px) { .hero-doors { grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px; } }

.hero-door-card {
  display: grid;
  grid-template-columns: 8px minmax(0, 1fr);
  gap: 12px;
  background: var(--ds-white);
  color: var(--ds-primary-900);
  border-radius: var(--ds-radius-md);
  padding: 12px 14px 12px 0;
  text-decoration: none;
  min-height: 60px;
  overflow: hidden;
  box-shadow: var(--ds-shadow-sm);
  transition: box-shadow 0.18s ease;
}
.hero-door-card-jamb {
  align-self: stretch;
  background: var(--ds-primary-900);
  border-radius: var(--ds-radius-md) 0 0 var(--ds-radius-md);
  transition: background-color 0.18s ease, width 0.18s ease;
}
.hero-door-card:hover .hero-door-card-jamb,
.hero-door-card:focus-visible .hero-door-card-jamb { background: var(--ds-primary-500); }
.hero-door-card:hover { box-shadow: var(--ds-shadow-lg); color: var(--ds-primary-900); text-decoration: none; }
.hero-door-card:focus-visible { outline: 3px solid var(--ds-accent); outline-offset: 2px; }
.hero-door-card-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.hero-door-card-label { font-family: var(--ds-font-display); font-weight: 700; font-size: 0.98rem; line-height: 1.25; }
.hero-door-card-next { color: var(--ds-neutral-500); font-size: 0.82rem; }

.hero-neutral-ctas { display: flex; flex-wrap: wrap; gap: 0.75rem; }

.btn-outline-light {
  background: transparent;
  color: var(--ds-white);
  border: 1px solid rgba(255, 255, 255, 0.55);
}
.btn-outline-light:hover,
.btn-outline-light:focus-visible {
  background: rgba(255, 255, 255, 0.12);
  border-color: var(--ds-white);
  color: var(--ds-white);
}

.cta-lead { font-size: 0.95rem !important; margin-bottom: 0.5rem !important; opacity: 0.75 !important; }

@media (prefers-reduced-motion: reduce) {
  .hero-door-card, .hero-door-card-jamb { transition: none; }
}

/* ==================== Section Headers ==================== */
.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-header h2 {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  color: #0a2540;
}

.section-header p {
  font-size: 1.2rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
}

/* ==================== "In action" (door-driven) ==================== */
.video-demo-section {
  padding: clamp(64px, 9vw, 100px) 0;
  background: var(--ds-white);
}

.video-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2rem;
}

.video-card {
  background: var(--ds-white);
  border: 1px solid var(--ds-neutral-200);
  border-radius: var(--ds-radius-xl);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* The door's leading recording carries the brand edge. */
.video-card--lead { border-top: 4px solid var(--ds-primary-500); }

.video-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: var(--ds-primary-900);
}

.video-player {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-info { padding: 1.5rem 1.75rem 1.75rem; }

.video-info h3 {
  font-family: var(--ds-font-display);
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: var(--ds-primary-900);
}

.video-info p {
  color: var(--ds-neutral-700);
  margin-bottom: 1rem;
  line-height: 1.6;
}

.video-highlights {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.video-highlights li {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  color: var(--ds-neutral-700);
  font-size: 0.95rem;
}

.video-highlights i { color: var(--ds-accent); font-size: 0.85rem; }

/* ==================== Workflow: a real sequence ==================== */
.integration-flow {
  padding: clamp(64px, 9vw, 100px) 0;
  background: var(--ds-neutral-50);
  border-top: 1px solid var(--ds-neutral-200);
}

/*
 * One row of four. Each step owns a 44px node; the connector is drawn by
 * every step EXCEPT the last, from the right edge of its node across the
 * column gap to the next node's left edge — so the line always lands on a
 * node and never points at empty space.
 */
.flow-track {
  --node: 44px;
  --gap: 2rem;
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--gap);
  counter-reset: none;
}

.flow-step {
  position: relative;
  min-width: 0;
}

.flow-step:not(:last-child)::after {
  content: '';
  position: absolute;
  top: calc(var(--node) / 2 - 1px);
  left: calc(var(--node) + 10px);
  right: calc(var(--gap) * -1 + 10px);
  height: 2px;
  background: linear-gradient(90deg, var(--ds-primary-500), var(--ds-primary-400));
  border-radius: 2px;
}

.flow-node {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--node);
  height: var(--node);
  border-radius: 50%;
  background: var(--ds-white);
  border: 2px solid var(--ds-primary-500);
  color: var(--ds-primary-900);
  font-family: var(--ds-font-display);
  font-weight: 800;
  font-size: 1.05rem;
  margin-bottom: 1rem;
}

/* The final step is where the visitor ends up: a filled node. */
.flow-step:last-child .flow-node {
  background: var(--ds-accent);
  border-color: var(--ds-accent);
  color: var(--ds-white);
}

.flow-text h3 {
  font-family: var(--ds-font-display);
  font-size: 1.12rem;
  font-weight: 700;
  color: var(--ds-primary-900);
  margin-bottom: 0.4rem;
  line-height: 1.3;
}

.flow-text p {
  font-size: 0.95rem;
  color: var(--ds-neutral-700);
  line-height: 1.6;
  margin: 0;
}

/* ==================== Pricing (DoorPlans) ==================== */
.pricing-tiers {
  padding: clamp(64px, 9vw, 100px) 0;
  background: var(--ds-white);
  border-top: 1px solid var(--ds-neutral-200);
}

.home-plans { max-width: 1080px; margin: 0 auto; }

.pricing-custom-cta {
  margin-top: 2.5rem;
  text-align: center;
  color: var(--ds-neutral-700);
}

.pricing-custom-cta p {
  margin-bottom: 1rem;
  font-size: 1.05rem;
}

.pricing-custom-cta .btn {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

/* ==================== Final CTA ==================== */
.final-cta {
  padding: 100px 0;
  background: #0a2540;
  color: white;
}

.cta-box {
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
}

.cta-box h2 {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
}

.cta-box p {
  font-size: 1.3rem;
  margin-bottom: 3rem;
  opacity: 0.95;
}

.cta-buttons {
  display: flex;
  gap: 2rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-large {
  padding: 1.2rem 2.5rem !important;
  font-size: 1.1rem !important;
  font-weight: 700 !important;
}

/* ==================== Responsive Design ==================== */
@media (max-width: 1024px) {
  .section-header h2 { font-size: 2.5rem; }
}

@media (max-width: 900px) {
  .video-grid { grid-template-columns: 1fr; }
}

/* ≤768px: the sequence becomes a vertical list; the connector runs down
   from each node to the next one. */
@media (max-width: 768px) {
  .hero-enhanced { padding-top: 100px; }
  .section-header { margin-bottom: 2.5rem; }
  .section-header h2 { font-size: 2rem; }

  .flow-track {
    grid-template-columns: 1fr;
    --gap: 1.75rem;
    max-width: 560px;
    margin: 0 auto;
  }
  .flow-step {
    display: grid;
    grid-template-columns: var(--node) minmax(0, 1fr);
    column-gap: 1rem;
    align-items: start;
  }
  .flow-node { margin-bottom: 0; }
  .flow-step:not(:last-child)::after {
    top: calc(var(--node) + 8px);
    bottom: calc(var(--gap) * -1 + 8px);
    left: calc(var(--node) / 2 - 1px);
    right: auto;
    width: 2px;
    height: auto;
    background: linear-gradient(180deg, var(--ds-primary-500), var(--ds-primary-400));
  }
  .flow-text { padding-top: 0.55rem; }

  .pricing-custom-cta { padding: 0 1rem; }
  .cta-box h2 { font-size: 2rem; }
  .cta-buttons { flex-direction: column; }
  .btn-large { width: 100%; }
}

@media (max-width: 480px) {
  .video-info { padding: 1.25rem; }
}
</style>
